// Adaptive-slice paginator for the WxCC run-search-query roots.
//
// Problem: `contactAIAgentSession` / `taskDetails` / `taskLegDetails` return
// at most 500 rows per call and the server exposes NO pagination arg
// (verified 2026-10-26: after/cursor/offset/skip/limit/first/size/page/
// pageSize all rejected or ignored). The schema includes `pageInfo.endCursor`
// but it's cosmetic — the backend doesn't accept it as an input.
//
// Solution: client-side adaptive bisection. Pull the whole window first; if
// the server flags `hasNextPage: true`, split the window in half and recurse.
// Repeat until every leaf slice is non-truncated. Dedup by the primary key
// (sessionId / taskId / legId) at merge time in case bisection boundaries
// land exactly on a session's start timestamp.
//
// Design properties:
//   * Guaranteed complete. Any truncation is retried narrower.
//   * Serialised, rate-limited at 2.2s per call (Cisco API cap).
//   * Progress-reporting via onProgress callback — UI can show live counts
//     + bisections + rows loaded.
//   * Cancellable via AbortSignal.
//   * Minimum slice guard: never slice narrower than MIN_SLICE_MS (1 minute
//     by default). A 1-minute slice that still truncates means something is
//     fundamentally wrong (not a window-cap problem) — surface it rather
//     than infinite-recurse.
//   * Starts coarse (whole window first). Fast path when there's little
//     data; slow path only when needed.

import { runSearchQuery } from './wxccSearchClient';

const RATE_GAP_MS = 2200;      // Cisco API: ~1 req / 2s. Stay under the cap.
const MIN_SLICE_MS = 60_000;   // 1-minute slice floor.
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Pull every row of one search-query root across the window using adaptive
 * bisection. Returns a `{ rows, meta }` object with full data (truncated=false
 * is the normal exit — truncated=true means a leaf slice at MIN_SLICE_MS still
 * reported hasNextPage, which is pathological).
 *
 * @param {string} query - GraphQL text (must take $from/$to Long args and
 *                         return rootKey.{listKey, pageInfo { hasNextPage }}).
 * @param {string} rootKey - Top-level field name, e.g. "contactAIAgentSession".
 * @param {string} listKey - Nested list field name, e.g. "data" / "tasks" / "taskLegs".
 * @param {string} primaryKey - Property to dedup on, e.g. "sessionId" / "id".
 * @param {object} args - { from: number, to: number, onProgress, signal }
 * @returns {Promise<{ rows, meta: { slicesTotal, bisections, truncatedLeaves, firstError } }>}
 */
export async function fetchAllPaged(query, rootKey, listKey, primaryKey, { from, to, onProgress, signal } = {}) {
  if (from == null || to == null) throw new Error('fetchAllPaged: from/to required.');

  const dedup = new Map();
  const meta = { slicesTotal: 0, bisections: 0, truncatedLeaves: 0, firstError: null };

  async function fetchOne(sliceFrom, sliceTo) {
    if (signal?.aborted) throw new Error('aborted');
    meta.slicesTotal += 1;
    onProgress?.({ ...meta, rowsLoaded: dedup.size, phase: 'fetching', sliceFrom, sliceTo });

    let res;
    try {
      res = await runSearchQuery(query, { from: sliceFrom, to: sliceTo });
    } catch (err) {
      // Record the first error for the UI to surface, but keep walking —
      // a transient 429 on one slice shouldn't blow up the whole fetch.
      if (!meta.firstError) meta.firstError = err?.message ?? String(err);
      await sleep(RATE_GAP_MS);
      return { rows: [], hasNext: false };
    }
    const root = res?.[rootKey] ?? {};
    const rows = root?.[listKey] ?? [];
    const hasNext = !!root?.pageInfo?.hasNextPage;
    return { rows, hasNext };
  }

  async function walk(sliceFrom, sliceTo, depth = 0) {
    if (signal?.aborted) return;
    const { rows, hasNext } = await fetchOne(sliceFrom, sliceTo);
    for (const row of rows) {
      const key = row?.[primaryKey];
      if (key != null) dedup.set(key, row);
    }
    onProgress?.({ ...meta, rowsLoaded: dedup.size, phase: 'merging', sliceFrom, sliceTo });
    await sleep(RATE_GAP_MS);

    if (!hasNext) return;

    const sliceMs = sliceTo - sliceFrom;
    if (sliceMs <= MIN_SLICE_MS) {
      // Pathological: a 1-minute window still truncated. Give up on this
      // leaf — surfacing it in meta so the UI can warn.
      meta.truncatedLeaves += 1;
      return;
    }

    // Bisect. We re-fetch the LEFT half rather than trying to subtract rows
    // we already have, because the server's result ordering is undocumented
    // and we can't safely assume "we got the earliest 500" or "we got the
    // latest 500" without more probing. Overlap is paid in dedup at merge.
    meta.bisections += 1;
    const mid = Math.floor((sliceFrom + sliceTo) / 2);
    await walk(sliceFrom, mid, depth + 1);
    if (signal?.aborted) return;
    await walk(mid, sliceTo, depth + 1);
  }

  await walk(from, to);

  onProgress?.({ ...meta, rowsLoaded: dedup.size, phase: 'done' });

  return {
    rows: [...dedup.values()],
    meta: {
      slicesTotal: meta.slicesTotal,
      bisections: meta.bisections,
      truncatedLeaves: meta.truncatedLeaves,
      firstError: meta.firstError,
      truncated: meta.truncatedLeaves > 0,
    },
  };
}
